import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '@core/services/auth.service';
import { HeaderService } from '@core/services/header.service';
import { SidebarService } from '@core/services/sidebar.service';
import { CompanySettingsService } from '@core/services/company-settings.service';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { NotificationService, AppNotification } from '@core/services/notification.service';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {

  title: string = 'Dashboard';
  currentUser: any = null;
  activeTab: string = 'overview';
  showSettingsPopup: boolean = false;
  showProfilePanel: boolean = false;
  showChangePassword: boolean = false;
  cpForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
  cpSubmitting = false;
  cpShowCurrent = false;
  cpShowNew = false;
  cpShowConfirm = false;
  todayDate: string = '';
  companyName: string = '';
  reportingManagerName: string = '';

  showNotifPanel = false;
  notifications: AppNotification[] = [];
  unreadCount = 0;

  constructor(
    private router: Router,
    private authService: AuthService,
    private headerService: HeaderService,
    private sidebarService: SidebarService,
    private companySettingsService: CompanySettingsService,
    private apiService: ApiService,
    private snackbar: SnackbarService,
    public notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.headerService.subscribe((title: string) => {
      this.title = title;
    });
    this.currentUser = this.authService.getCurrentUser();
    this.companySettingsService.settings$.subscribe(s => {
      if (s?.companyName) this.companyName = s.companyName;
    });
    if (this.currentUser?.id) {
      this.apiService.get('/employees/' + this.currentUser.id).subscribe({
        next: (emp: any) => {
          // Pull the real profile photo (not stored in the login payload) so the header
          // avatar can show it instead of always falling back to initials.
          if (emp?.profilePicture) this.currentUser = { ...this.currentUser, profilePicture: emp.profilePicture };
          const managerId = emp?.managerId;
          if (managerId) {
            this.apiService.get('/employees/' + managerId).subscribe({
              next: (mgr: any) => {
                this.reportingManagerName = mgr ? `${mgr.firstName || ''} ${mgr.lastName || ''}`.trim() : '';
              },
              error: () => {}
            });
          }
        },
        error: () => {}
      });
    }
    const now = new Date();
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    this.todayDate = `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
    this.detectTab();
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(() => this.detectTab());

    this.notificationService.start();
    this.notificationService.unread$.subscribe(c => this.unreadCount = c);
    this.notificationService.items$.subscribe(items => this.notifications = items);
  }

  toggleNotifPanel(): void {
    this.showNotifPanel = !this.showNotifPanel;
    if (this.showNotifPanel) {
      this.showProfilePanel = false;
      this.notificationService.refreshList();
    }
  }

  closeNotifPanel(): void {
    this.showNotifPanel = false;
  }

  onNotifClick(n: AppNotification): void {
    this.notificationService.markRead(n);
    if (n.link) {
      this.showNotifPanel = false;
      this.router.navigateByUrl(n.link);
    }
  }

  markAllNotifsRead(): void {
    this.notificationService.markAllRead();
  }

  notifIcon(type: string): string {
    if (!type) return 'notifications';
    if (type.includes('approved')) return 'check_circle';
    if (type.includes('rejected')) return 'cancel';
    if (type.includes('pending')) return 'pending_actions';
    if (type.includes('candidate')) return 'person_add';
    if (type.includes('payslip') || type.includes('payroll')) return 'payments';
    if (type.includes('announcement')) return 'campaign';
    return 'notifications';
  }

  timeAgo(iso: string): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return mins + 'm ago';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    const days = Math.floor(hrs / 24);
    if (days < 7) return days + 'd ago';
    return new Date(iso).toLocaleDateString();
  }

  private detectTab(): void {
    const url = this.router.url;
    if (url.includes('/dashboard')) this.activeTab = 'dashboard';
    else if (url.includes('/overview')) this.activeTab = 'overview';
    else this.activeTab = '';
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'overview') this.router.navigate(['/overview']);
    else if (tab === 'dashboard') this.router.navigate(['/dashboard']);
    else if (tab === 'calendar') this.router.navigate(['/overview'], { queryParams: { tab: 'attendance' } });
    else if (tab === 'delegation') this.router.navigate(['/overview'], { queryParams: { tab: 'approvals' } });
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  logout(): void {
    this.authService.logout();
  }

  goToProfile(): void {
    this.showProfilePanel = false;
    this.router.navigate(['/profile']);
  }

  toggleProfilePanel(): void {
    this.showProfilePanel = !this.showProfilePanel;
  }

  closeProfilePanel(): void {
    this.showProfilePanel = false;
  }

  goToSettings(): void {
    this.router.navigate(['/settings']);
  }

  canAccessSettings(): boolean {
    const role = (this.currentUser?.role || '').toLowerCase();
    return ['admin', 'super_admin'].includes(role);
  }

  closeSettingsPopup(): void {
    this.showSettingsPopup = false;
  }

  openChangePassword(): void {
    this.cpForm = { currentPassword: '', newPassword: '', confirmPassword: '' };
    this.cpShowCurrent = false;
    this.cpShowNew = false;
    this.cpShowConfirm = false;
    this.showChangePassword = true;
    this.showProfilePanel = false;
  }

  closeChangePassword(): void {
    this.showChangePassword = false;
  }

  submitChangePassword(): void {
    if (!this.cpForm.currentPassword) { this.snackbar.showFailure('Enter your current password'); return; }
    if (this.cpForm.newPassword.length < 8) { this.snackbar.showFailure('New password must be at least 8 characters'); return; }
    if (this.cpForm.newPassword !== this.cpForm.confirmPassword) { this.snackbar.showFailure('Passwords do not match'); return; }
    if (this.cpForm.newPassword === this.cpForm.currentPassword) { this.snackbar.showFailure('New password must differ from current password'); return; }

    this.cpSubmitting = true;
    this.apiService.post('/employees/change-password', {
      currentPassword: this.cpForm.currentPassword,
      newPassword: this.cpForm.newPassword
    }).subscribe({
      next: () => {
        this.cpSubmitting = false;
        this.showChangePassword = false;
        this.snackbar.showSuccess('Password changed successfully');
      },
      error: (err: any) => {
        this.cpSubmitting = false;
        const msg = err?.error?.message || 'Failed to change password';
        this.snackbar.showFailure(msg);
      }
    });
  }

  getPasswordStrength(): { label: string; color: string; width: string } {
    const p = this.cpForm.newPassword;
    if (!p) return { label: '', color: '#e5e7eb', width: '0%' };
    let score = 0;
    if (p.length >= 8) score++;
    if (p.length >= 12) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 1) return { label: 'Weak', color: '#ef4444', width: '25%' };
    if (score <= 2) return { label: 'Fair', color: '#f59e0b', width: '50%' };
    if (score <= 3) return { label: 'Good', color: '#3b82f6', width: '75%' };
    return { label: 'Strong', color: '#10b981', width: '100%' };
  }

  getInitials(): string {
    if (!this.currentUser) return '';
    const f = (this.currentUser.firstName || '')[0] || '';
    const l = (this.currentUser.lastName || '')[0] || '';
    return (f + l).toUpperCase();
  }
}
